import { FileSystemAdapter } from '../utils/file-system-adapter.js';
import path from 'path';
import { PathnameService } from './pathname-service.js';

/**
 * Centralizes all filesystem operations with atomic guarantees and proper error handling.
 * This service is responsible for:
 * - Atomic file operations
 * - Directory management
 * - File search operations
 * - Temporary file handling
 * - Backup management
 */
export class FileService {
  constructor(
    private fs: FileSystemAdapter,
    private pathnameService: PathnameService
  ) {}

  // Core Operations

  async readFile(filePath: string): Promise<string> {
    if (!await this.fs.exists(filePath)) {
      throw new Error(`File not found: ${filePath}`);
    }
    return this.fs.readFile(filePath);
  }

  async writeFile(filePath: string, content: string): Promise<void> {
    // Use temp file for atomic write
    return this.withTempFile(filePath, async (tempPath) => {
      await this.fs.writeFile(tempPath, content);
    });
  }

  async moveFile(sourcePath: string, targetPath: string): Promise<void> {
    // Validate paths
    if (!await this.fs.exists(sourcePath)) {
      throw new Error(`Source file not found: ${sourcePath}`);
    }
    if (await this.fs.exists(targetPath)) {
      throw new Error(`Target file already exists: ${targetPath}`);
    }

    // Ensure target directory exists
    await this.ensureParentDir(targetPath);

    // Use temp file for atomic move
    const tempPath = `${targetPath}.tmp`;
    try {
      // First copy to temp file
      await this.fs.move(sourcePath, tempPath);
      // Then move temp file to target
      await this.fs.move(tempPath, targetPath);
    } catch (error) {
      // Clean up temp file on error
      try {
        if (await this.fs.exists(tempPath)) {
          await this.fs.remove(tempPath);
        }
      } catch (cleanupError) {
        // Ignore cleanup errors
      }
      throw error;
    }
  }

  async atomicMove(sourcePath: string, targetPath: string, content: string): Promise<void> {
    // Ensure source file exists
    if (!await this.fs.exists(sourcePath)) {
      throw new Error(`File not found: ${sourcePath}`);
    }

    // Create temp file with new content
    const tempPath = `${targetPath}.tmp`;
    try {
      // Write new content to temp file
      await this.fs.writeFile(tempPath, content);
      
      // Move temp file to target
      await this.fs.move(tempPath, targetPath);
      
      // Remove source file after successful move
      if (await this.fs.exists(sourcePath)) {
        await this.fs.remove(sourcePath);
      }
    } catch (error) {
      // Clean up temp file on error
      try {
        if (await this.fs.exists(tempPath)) {
          await this.fs.remove(tempPath);
        }
      } catch (cleanupError) {
        // Ignore cleanup errors
      }
      throw error;
    }
  }

  // Atomic Operations

  async withTempFile<T>(filePath: string, operation: (tempPath: string) => Promise<T>): Promise<T> {
    const tempPath = `${filePath}.tmp`;
    try {
      const result = await operation(tempPath);
      if (await this.fs.exists(tempPath)) {
        await this.fs.move(tempPath, filePath);
      }
      return result;
    } finally {
      // Always try to cleanup temp file
      if (await this.fs.exists(tempPath)) {
        await this.fs.remove(tempPath);
      }
    }
  }

  async withBackup<T>(filePath: string, operation: () => Promise<T>): Promise<T> {
    const backupPath = `${filePath}.bak`;
    
    // Create backup if file exists by copying content
    if (await this.fs.exists(filePath)) {
      try {
        const content = await this.fs.readFile(filePath);
        await this.fs.writeFile(backupPath, content);
      } catch (error) {
        throw new Error('Backup creation failed');
      }
    }

    try {
      // Run the operation
      const result = await operation();
      
      // Remove backup on success
      if (await this.fs.exists(backupPath)) {
        try {
          await this.fs.remove(backupPath);
        } catch (cleanupError) {
          // Operation succeeded but cleanup failed
          // We want to preserve the backup file and throw cleanup error
          throw new Error('Cleanup failed');
        }
      }
      
      return result;
    } catch (error) {
      const operationError = error as Error;
      
      // Don't attempt cleanup if operation failed with cleanup error
      if (operationError.message === 'Cleanup failed') {
        throw operationError;
      }
      
      // Restore from backup on error if it exists
      if (await this.fs.exists(backupPath)) {
        try {
          // Read backup content first to ensure we can read it
          const backupContent = await this.fs.readFile(backupPath);
          
          // Remove original file if it exists
          if (await this.fs.exists(filePath)) {
            await this.fs.remove(filePath);
          }
          
          // Write backup content back to original file
          await this.fs.writeFile(filePath, backupContent);
        } catch (restoreError) {
          // If restore fails, preserve backup and throw restore error
          throw new Error('Restore failed');
        }
      }
      
      // If we get here, either:
      // 1. There was no backup to restore from
      // 2. Restore succeeded (but we keep the backup in case cleanup fails)
      throw operationError;
    }
  }

  // Directory Operations

  async ensureDir(dirPath: string): Promise<void> {
    await this.fs.ensureDir(dirPath);
  }

  async ensureParentDir(filePath: string): Promise<void> {
    await this.ensureDir(path.dirname(filePath));
  }

  // File Search Operations

  async findFile(filename: string, searchPaths: string[]): Promise<string | undefined> {
    for (const dir of searchPaths) {
      const filePath = path.join(dir, filename);
      if (await this.fs.exists(filePath)) {
        return filePath;
      }
    }
    return undefined;
  }

  async findFiles(predicate: (filename: string) => boolean, searchPaths: string[]): Promise<string[]> {
    const results: string[] = [];
    
    for (const dir of searchPaths) {
      if (!await this.fs.exists(dir)) {
        continue;
      }
      
      const files = await this.fs.readdir(dir);
      for (const file of files) {
        if (predicate(file)) {
          results.push(path.join(dir, file));
        }
      }
    }
    
    return results;
  }
} 